<script lang="ts">
  import InboxWorkflowDefaultBulkActions from "@kontextwerk/control-web-frame/shared/modules/inbox-workflow-default-bulk-actions"
  import type { InboxWorkflowTableSelectionContext } from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
    type TableSortState,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import type { InboxCustomStepRowPresentation } from "../../../../../../_shared/_logic/custom-step-rows"
  import PrintSelectionAppointmentCell from "./_modules/print-selection-appointment-cell"
  import PrintSelectionOrderCell from "./_modules/print-selection-order-cell"
  import PrintSelectionPrintCell from "./_modules/print-selection-print-cell"
  import PrintSelectionRecipientCell from "./_modules/print-selection-recipient-cell"
  import PrintSelectionRecommendationCell from "./_modules/print-selection-recommendation-cell"
  import type { PrintSelectionPreviewRow } from "../../../../../../_shared/_logic/print-selection-preview"
  import type {
    PrintSelectionState,
    PrintSelectionValue,
  } from "../../_shared/_logic/print-selection-state"

  type PrintSelectionTableRow = PrintSelectionPreviewRow & {
    id: string
    printSelectionEnabled: boolean
  }
  type PrintSelectionTableRowControlCommand = {
    kind: "reset" | "remove" | "restore"
    disabled?: boolean
    onClick?: () => void | Promise<void>
  }
  type PrintSelectionChangeDetail = {
    selectedIds: string[]
  }

  let {
    rows,
    selection,
    disabled = false,
    loading = false,
    rowPresentations = {},
    removedActionCaseIds = [],
    printSelectionEnabledItemIds = [],
    selectedIds = [],
    workflowActionsEnabled = false,
    workflowReadOnly = false,
    onChange,
    onSelectionChange = undefined,
    onResetRows = undefined,
    onRemoveRows = undefined,
    onRestoreRow = undefined,
  }: {
    rows: PrintSelectionPreviewRow[]
    selection: PrintSelectionState
    disabled?: boolean
    loading?: boolean
    rowPresentations?: Record<string, InboxCustomStepRowPresentation>
    removedActionCaseIds?: string[]
    printSelectionEnabledItemIds?: string[]
    selectedIds?: string[]
    workflowActionsEnabled?: boolean
    workflowReadOnly?: boolean
    onChange: (itemId: string, value: PrintSelectionValue) => void
    onSelectionChange?: (selectedIds: string[]) => void
    onResetRows?: (rows: PrintSelectionPreviewRow[]) => void
    onRemoveRows?: (rows: PrintSelectionPreviewRow[]) => void
    onRestoreRow?: (row: PrintSelectionPreviewRow) => void
  } = $props()

  const columns: ColumnDef[] = [
    {
      id: "number",
      label: "Nr.",
      align: "left",
      sortable: true,
      sortKey: "number",
      sortType: "number",
      defaultSortDir: "asc",
      getSortValue: (row) => (row as PrintSelectionTableRow).number,
    },
    { id: "print", label: "Drucken", sortable: false },
    { id: "recommendation", label: "Empfehlung", sortable: false },
    { id: "order", label: "Auftrag", sortable: false },
    { id: "appointment", label: "Termin", sortable: false },
    { id: "recipient", label: "Empfänger", sortable: false },
  ]

  const removedActionCaseIdSet = $derived(new Set(removedActionCaseIds))
  const printSelectionEnabledItemIdSet = $derived(new Set(printSelectionEnabledItemIds))
  const tableRows = $derived(
    rows.map((row) => ({
      ...row,
      id: row.letter_delivery_batch_item_id,
      printSelectionEnabled: printSelectionEnabledItemIdSet.has(row.letter_delivery_batch_item_id),
    })),
  )
  const refreshKey = $derived(
    JSON.stringify(
      tableRows.map((row) => ({
        id: row.letter_delivery_batch_item_id,
        printable: row.printable,
        printSelectionEnabled: row.printSelectionEnabled,
        disabledReason: row.disabled_reason ?? null,
        recommendationReasonCode: row.recommendation_reason_code,
        recommendationText: row.recommendation_text,
        recommendationTagLabel: row.recommendation_tag_label,
        recommendationTagColor: row.recommendation_tag_color,
        recommendationShortReason: row.recommendation_short_reason,
        statusLabel: row.status_label,
        presentation: row.action_case_id
          ? {
              disabled: rowPresentations[row.action_case_id]?.disabled ?? false,
              status: rowPresentations[row.action_case_id]?.statusLabel ?? "",
              removedCurrent: rowPresentations[row.action_case_id]?.removedCurrent ?? false,
            }
          : null,
        removed: row.action_case_id ? removedActionCaseIdSet.has(row.action_case_id) : false,
      })),
    ),
  )

  const numberSort: NonNullable<TableSortState> = {
    columnId: "number",
    key: "number",
    dir: "asc",
  }
  const getRows: TableGetRows = ({ limit, offset, sort }) =>
    getSortedTablePage({
      rows: tableRows,
      columns,
      sort: sort ?? numberSort,
      limit,
      offset,
    })

  const workflowActionable = (row: PrintSelectionTableRow): boolean => {
    if (!workflowActionsEnabled || workflowReadOnly) return false

    const actionCaseId = String(row.action_case_id ?? "").trim()

    if (!actionCaseId || removedActionCaseIdSet.has(actionCaseId)) return false

    const presentation = rowPresentations[actionCaseId]

    return presentation
      ? presentation.isCompatible &&
          presentation.isOpen &&
          !presentation.isResolved &&
          !presentation.disabled
      : true
  }

  const rowState = (value: DataRow) => {
    const preview = value as PrintSelectionTableRow
    const actionCaseId = String(preview.action_case_id ?? "").trim()
    const presentation = actionCaseId ? rowPresentations[actionCaseId] : null
    const removed = actionCaseId ? removedActionCaseIdSet.has(actionCaseId) : false

    if (removed) {
      const reason = "Diese Zeile wird in diesem Schritt ausgelassen."

      return {
        disabled: true,
        selectable: false,
        muted: true,
        disabledReason: reason,
        mutedReason: reason,
      }
    }

    if (preview.printSelectionEnabled || workflowActionable(preview)) {
      return { selectable: !disabled && !loading }
    }

    if (presentation?.disabled || workflowReadOnly || !preview.printable) {
      const reason =
        presentation?.disabledReason ??
        preview.disabled_reason ??
        "Dieser Schritt ist schreibgeschützt."

      return {
        disabled: true,
        selectable: false,
        muted: true,
        disabledReason: reason,
        mutedReason: reason,
      }
    }

    return { selectable: false }
  }

  const rowControlsFor = (value: DataRow): PrintSelectionTableRowControlCommand[] => {
    const row = value as PrintSelectionTableRow

    if (!workflowActionsEnabled || workflowReadOnly) return []

    const actionCaseId = String(row.action_case_id ?? "").trim()

    if (!actionCaseId) return []

    const removed = removedActionCaseIdSet.has(actionCaseId)

    if (removed) {
      return [
        {
          kind: "restore",
          disabled,
          onClick: () => onRestoreRow?.(row),
        },
      ]
    }

    if (!workflowActionable(row)) return []

    return [
      {
        kind: "reset",
        disabled,
        onClick: () => onResetRows?.([row]),
      },
      {
        kind: "remove",
        disabled,
        onClick: () => onRemoveRows?.([row]),
      },
    ]
  }

  const handleSelectionChange = (detail: PrintSelectionChangeDetail) => {
    onSelectionChange?.(detail.selectedIds)
  }

  const selectedTableRows = (rows: DataRow[]): PrintSelectionTableRow[] =>
    rows as PrintSelectionTableRow[]
</script>

{#snippet batchActions(context: InboxWorkflowTableSelectionContext)}
  <InboxWorkflowDefaultBulkActions
    selectedRows={selectedTableRows(context.selectedRows)}
    resetRows={(selectedRows) => onResetRows?.(selectedRows)}
    removeRows={(selectedRows) => onRemoveRows?.(selectedRows)}
    clearSelection={context.clearSelection}
    readOnly={!workflowActionsEnabled || workflowReadOnly}
    disabled={disabled || loading}
    isRowDisabled={(row) => !workflowActionable(row)}
  />
{/snippet}

<Table
  {columns}
  {getRows}
  {loading}
  {refreshKey}
  defaultSort={numberSort}
  pageSize={50}
  theme="light"
  mode="table-scroll"
  showFilters={false}
  loadingRowCount={6}
  getRowState={rowState}
  enableSelection={true}
  {selectedIds}
  {batchActions}
  batchActionsSurfaceTone="dark"
  showActions={workflowActionsEnabled && !workflowReadOnly}
  rowControls={workflowActionsEnabled && !workflowReadOnly ? rowControlsFor : undefined}
  onSelectionChange={handleSelectionChange}
  rowTestId={(row) =>
    `letter-delivery-print-row.${(row as PrintSelectionTableRow).letter_delivery_batch_item_id}`}
  cellTestId={(row, column) =>
    `letter-delivery-print-row.${(row as PrintSelectionTableRow).letter_delivery_batch_item_id}.${column.id}`}
>
  {#snippet tableCell({ row, column })}
    {@const preview = row as PrintSelectionTableRow}
    {#if column.id === "number"}
      {preview.number}
    {:else if column.id === "print"}
      <PrintSelectionPrintCell
        row={preview}
        {selection}
        disabled={disabled || !preview.printSelectionEnabled}
        {onChange}
      />
    {:else if column.id === "recommendation"}
      <PrintSelectionRecommendationCell row={preview} />
    {:else if column.id === "order"}
      <PrintSelectionOrderCell row={preview} />
    {:else if column.id === "appointment"}
      <PrintSelectionAppointmentCell row={preview} />
    {:else if column.id === "recipient"}
      <PrintSelectionRecipientCell row={preview} />
    {/if}
  {/snippet}
</Table>
